/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { api } from "../../../lib/auth";
import { mediaUrl } from "../../../lib/media";
import { toast } from "sonner";
import {
  Users, Phone, Mail, MapPin, Plus, X, Search, Loader2, MessageSquare,
  TrendingUp, Calendar, Tag, Trash2, ChevronRight, ArrowRight, Clock, Check,
} from "lucide-react";

const ngn = (n) => `₦${Number(n || 0).toLocaleString()}`;

const STAGES = [
  { k: "new", lbl: "New", color: "bg-zinc-100 text-zinc-700" },
  { k: "contacted", lbl: "Contacted", color: "bg-blue-100 text-blue-700" },
  { k: "interested", lbl: "Interested", color: "bg-violet-100 text-violet-700" },
  { k: "inspection", lbl: "Inspection", color: "bg-amber-100 text-amber-700" },
  { k: "negotiation", lbl: "Negotiation", color: "bg-orange-100 text-orange-700" },
  { k: "closed_won", lbl: "Won", color: "bg-emerald-100 text-emerald-700" },
  { k: "closed_lost", lbl: "Lost", color: "bg-red-100 text-red-700" },
];

const SOURCES = ["direct", "referral", "social", "walk-in", "online", "ad"];

export function LeadsView({ marketerOnly = false }) {
  const [items, setItems] = useState([]);
  const [stageF, setStageF] = useState("all");
  const [q, setQ] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [selected, setSelected] = useState(null);
  const [marketers, setMarketers] = useState([]);

  const load = async () => {
    try {
      const url = stageF === "all" ? "/t/re/leads" : `/t/re/leads?stage=${stageF}`;
      setItems((await api.get(url)).data || []);
    } catch { setItems([]); }
  };
  useEffect(() => { load(); }, [stageF]);
  useEffect(() => {
    if (!marketerOnly) api.get("/t/re/marketers").then((r) => setMarketers(r.data || [])).catch(() => {});
  }, []);

  const filtered = items.filter((l) => !q || (l.full_name || "").toLowerCase().includes(q.toLowerCase()) || (l.phone || "").includes(q));

  return (
    <div data-testid="re-leads-view">
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-black/40" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search leads…"
            className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-black/15 text-sm" data-testid="re-leads-search" />
        </div>
        <button onClick={() => setShowNew(true)} data-testid="re-lead-new-btn"
          className="px-4 py-2.5 rounded-lg bg-[#1FA868] text-white text-sm font-bold flex items-center gap-2"><Plus size={14} /> New Lead</button>
      </div>

      <div className="flex items-center gap-1.5 mb-4 overflow-x-auto pb-1">
        {[{ k: "all", lbl: "All", color: "bg-black text-white" }, ...STAGES].map((s) => (
          <button key={s.k} onClick={() => setStageF(s.k)} data-testid={`re-leads-filter-${s.k}`}
            className={`px-3 py-1.5 text-xs font-bold rounded-md whitespace-nowrap ${stageF === s.k ? "bg-black text-white" : "bg-black/5 text-black/65 hover:bg-black/10"}`}>
            {s.lbl}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.length === 0 ? (
          <div className="col-span-full bg-white rounded-2xl border border-black/8 p-12 text-center">
            <Users size={32} className="mx-auto text-black/25 mb-3" />
            <div className="font-bold text-black/70">No leads yet</div>
          </div>
        ) : filtered.map((l) => {
          const stage = STAGES.find((s) => s.k === l.stage) || STAGES[0];
          return (
            <div key={l.id} className="bg-white rounded-2xl border border-black/8 p-4 hover:shadow-lg transition-shadow cursor-pointer" data-testid={`re-lead-card-${l.id}`}
              onClick={() => setSelected(l)}>
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2.5">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 text-white flex items-center justify-center font-display font-extrabold">
                    {(l.full_name || "?")[0].toUpperCase()}
                  </div>
                  <div>
                    <div className="font-display font-extrabold text-sm text-black">{l.full_name}</div>
                    {l.phone && <div className="text-xs text-black/55">{l.phone}</div>}
                  </div>
                </div>
                <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${stage.color}`}>{stage.lbl}</span>
              </div>
              <div className="text-xs text-black/55 mt-2 flex flex-wrap items-center gap-2">
                {l.source && <span className="bg-black/5 px-1.5 py-0.5 rounded">{l.source}</span>}
                {(l.budget_min || l.budget_max) ? <span className="text-emerald-700 font-bold">{ngn(l.budget_min)} – {ngn(l.budget_max)}</span> : null}
                {l.last_follow_up_at && <span className="inline-flex items-center gap-1"><Clock size={10} /> {(l.last_follow_up_at || "").slice(0, 10)}</span>}
              </div>
              {l.notes && <div className="text-xs text-black/60 mt-2 line-clamp-2">{l.notes}</div>}
            </div>
          );
        })}
      </div>

      {showNew && <LeadEditor marketers={marketers} onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}
      {selected && <LeadDetail lead={selected} marketers={marketers} onClose={() => setSelected(null)} onUpdated={() => { load(); }} />}
    </div>
  );
}

function LeadEditor({ marketers, onClose, onSaved }) {
  const [f, setF] = useState({ full_name: "", phone: "", email: "", source: "direct", assigned_marketer_id: "", notes: "", budget_min: 0, budget_max: 0 });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!f.full_name.trim()) return toast.error("Name is required");
    setSaving(true);
    try {
      await api.post("/t/re/leads", f);
      toast.success("Lead created");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <Modal title="New Lead" onClose={onClose}>
      <div className="grid grid-cols-2 gap-3">
        <Field lbl="Full name *" full><Input value={f.full_name} onChange={(v) => setF({ ...f, full_name: v })} testid="lead-name" /></Field>
        <Field lbl="Phone"><Input value={f.phone} onChange={(v) => setF({ ...f, phone: v })} testid="lead-phone" /></Field>
        <Field lbl="Email"><Input value={f.email} onChange={(v) => setF({ ...f, email: v })} /></Field>
        <Field lbl="Source">
          <select value={f.source} onChange={(e) => setF({ ...f, source: e.target.value })} className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm">
            {SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
        <Field lbl="Assign marketer">
          <select value={f.assigned_marketer_id} onChange={(e) => setF({ ...f, assigned_marketer_id: e.target.value })} className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm" data-testid="lead-marketer">
            <option value="">None</option>
            {marketers.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        </Field>
        <Field lbl="Budget min (₦)"><Input type="number" value={f.budget_min} onChange={(v) => setF({ ...f, budget_min: parseFloat(v) || 0 })} /></Field>
        <Field lbl="Budget max (₦)"><Input type="number" value={f.budget_max} onChange={(v) => setF({ ...f, budget_max: parseFloat(v) || 0 })} /></Field>
        <Field lbl="Notes" full><textarea rows={3} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm" /></Field>
      </div>
      <Footer onCancel={onClose} onSave={submit} saving={saving} testid="lead-save" />
    </Modal>
  );
}

function LeadDetail({ lead, marketers, onClose, onUpdated }) {
  const [l, setL] = useState(lead);
  const [fu, setFu] = useState({ kind: "call", notes: "", outcome: "" });
  const [posting, setPosting] = useState(false);

  const setStage = async (stage) => {
    try { await api.patch(`/t/re/leads/${l.id}`, { stage }); setL({ ...l, stage }); onUpdated(); toast.success(`Moved to ${stage}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const reassign = async (mid) => {
    try { await api.patch(`/t/re/leads/${l.id}`, { assigned_marketer_id: mid }); setL({ ...l, assigned_marketer_id: mid }); onUpdated(); toast.success("Reassigned"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const addFu = async () => {
    if (!fu.notes && !fu.outcome) return toast.error("Add notes or outcome");
    setPosting(true);
    try {
      const r = await api.post(`/t/re/leads/${l.id}/follow-up`, fu);
      setL({ ...l, follow_ups: [...(l.follow_ups || []), r.data] });
      setFu({ kind: "call", notes: "", outcome: "" });
      onUpdated();
      toast.success("Follow-up logged");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setPosting(false); }
  };

  const stage = STAGES.find((s) => s.k === l.stage) || STAGES[0];
  return (
    <Modal title={l.full_name} onClose={onClose} wide>
      <div className="flex items-center gap-3 mb-5">
        <span className={`px-3 py-1 rounded-full text-xs font-bold ${stage.color}`}>{stage.lbl}</span>
        <select value={l.assigned_marketer_id || ""} onChange={(e) => reassign(e.target.value)} className="text-xs border border-black/15 rounded px-2 py-1">
          <option value="">Unassigned</option>
          {marketers.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
        </select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
        <div className="bg-[#FAFBFD] rounded-xl p-4">
          <div className="text-[11px] uppercase font-bold text-black/55 mb-2">Contact</div>
          {l.phone && <div className="text-sm flex items-center gap-2"><Phone size={12} /> {l.phone}</div>}
          {l.email && <div className="text-sm flex items-center gap-2 mt-1"><Mail size={12} /> {l.email}</div>}
        </div>
        <div className="bg-[#FAFBFD] rounded-xl p-4">
          <div className="text-[11px] uppercase font-bold text-black/55 mb-2">Details</div>
          <div className="text-xs text-black/70">Source: <strong>{l.source}</strong></div>
          {l.budget_min || l.budget_max ? <div className="text-xs text-black/70 mt-1">Budget: <strong>{ngn(l.budget_min)} – {ngn(l.budget_max)}</strong></div> : null}
        </div>
      </div>

      <div className="mb-5">
        <div className="text-[11px] uppercase font-bold text-black/55 mb-2">Move to stage</div>
        <div className="flex flex-wrap gap-1.5">
          {STAGES.map((s) => (
            <button key={s.k} onClick={() => setStage(s.k)} data-testid={`lead-stage-${s.k}`}
              className={`px-3 py-1.5 text-xs font-bold rounded-md ${l.stage === s.k ? s.color : "bg-black/5 text-black/65 hover:bg-black/10"}`}>
              {s.lbl}
            </button>
          ))}
        </div>
      </div>

      <div className="border-t border-black/8 pt-4">
        <div className="font-display font-extrabold text-lg text-black mb-3">Follow-ups</div>
        <div className="space-y-2 mb-4">
          {(l.follow_ups || []).map((f) => (
            <div key={f.id} className="bg-[#FAFBFD] rounded-lg p-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="font-bold text-xs uppercase">{f.kind} · {f.outcome || "logged"}</span>
                <span className="text-xs text-black/55">{(f.created_at || "").slice(0, 16).replace("T", " ")}</span>
              </div>
              {f.notes && <div className="mt-1 text-black/70 text-xs">{f.notes}</div>}
            </div>
          ))}
          {(l.follow_ups || []).length === 0 && <div className="text-xs text-black/45 text-center py-4">No follow-ups yet</div>}
        </div>

        <div className="grid grid-cols-3 gap-2">
          <select value={fu.kind} onChange={(e) => setFu({ ...fu, kind: e.target.value })} className="border border-black/15 rounded-lg px-2 py-2 text-sm" data-testid="lead-fu-kind">
            <option value="call">Call</option><option value="whatsapp">WhatsApp</option><option value="email">Email</option>
            <option value="meeting">Meeting</option><option value="inspection">Inspection</option><option value="sms">SMS</option>
          </select>
          <select value={fu.outcome} onChange={(e) => setFu({ ...fu, outcome: e.target.value })} className="border border-black/15 rounded-lg px-2 py-2 text-sm">
            <option value="">Outcome…</option>
            <option value="interested">Interested</option>
            <option value="not_interested">Not interested</option>
            <option value="callback">Callback</option>
            <option value="closed">Closed</option>
          </select>
          <button onClick={addFu} disabled={posting} data-testid="lead-fu-save"
            className="px-4 py-2 rounded-lg bg-[#1FA868] text-white text-sm font-bold disabled:opacity-50">{posting ? "…" : "Log"}</button>
        </div>
        <textarea value={fu.notes} onChange={(e) => setFu({ ...fu, notes: e.target.value })} rows={2} placeholder="Notes (optional)…"
          className="w-full mt-2 border border-black/15 rounded-lg px-3 py-2 text-sm" />
      </div>
    </Modal>
  );
}

function Modal({ title, onClose, children, wide }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className={`bg-white rounded-2xl shadow-2xl ${wide ? "max-w-3xl" : "max-w-2xl"} w-full max-h-[92vh] overflow-y-auto`} onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-black/8 flex items-center justify-between sticky top-0 bg-white z-10">
          <div className="font-display font-extrabold text-lg text-black">{title}</div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-black/5"><X size={16} /></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}
function Field({ lbl, full, children }) { return <div className={full ? "col-span-2" : ""}><label className="text-[11px] uppercase font-bold text-black/55">{lbl}</label><div className="mt-1">{children}</div></div>; }
function Input({ value, onChange, type = "text", placeholder, testid }) { return <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm" data-testid={testid} />; }
function Footer({ onCancel, onSave, saving, testid }) { return <div className="flex gap-2 pt-4 mt-4 border-t border-black/8"><button onClick={onCancel} className="flex-1 px-4 py-2.5 rounded-lg bg-black/5 text-sm font-bold text-black/70">Cancel</button><button onClick={onSave} disabled={saving} className="flex-1 px-4 py-2.5 rounded-lg bg-[#1FA868] text-white text-sm font-bold disabled:opacity-50" data-testid={testid}>{saving ? "Saving…" : "Save"}</button></div>; }
